/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import QueryDetailLink, {
  buildQueryDetailTarget,
  QueryDetailEditAction,
} from '../components/QueryDetailLink';
import AnalyticTable from 'components/kea/tables/AnalyticTable';
import CardContainer from 'components/kea/CardContainer';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import AnalyticsMetricBar from '../components/AnalyticsMetricBar';
import AnalyticsReportActions from '../components/AnalyticsReportActions';
import {
  loadAnalyticsReport,
  selectLostOpportunities,
  selectTimeframe,
  actions as analyticsActions,
} from 'slices/AnalyticsSlice';
import { rulesForQuery, useQueryRuleIndex } from '../components/useQueryRuleIndex';

/**
 * Bridge-backed Lost Opportunities page.
 *
 * Column labels use "Searches" (not "Users") because the plugin analytics table
 * has no user identity — event counts only.
 * The `refine` column is omitted — it is always 0 in the plugin model.
 *
 * No sidebar widgets: both legacy widgets for this page use dishonest labels.
 * - QueriesWithLowCTR: shows `queries.filteredTotal` = `$totalSearches` (event count)
 *   labeled as "Queries with low CTR" — does not count distinct queries.
 * - AffectedUsers: shows `users.filteredTotal` (search event count) as "Affected users".
 */
const LostOpportunitiesBridge = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const localizeMessage = useLocalizedMessage();
  const lostOpportunities = useSelector(selectLostOpportunities);
  const timeframe = useSelector(selectTimeframe);
  const { currentShop } = useSelector((store) => store.app);
  const { capabilities } = useShellAppContext();
  const queryRuleIndex = useQueryRuleIndex();
  const detailEnabled = Boolean(capabilities?.analyticsQueryDetailV1);
  const exportEnabled = Boolean(capabilities?.analyticsReportExportV1);
  const returnTo = `${location.pathname}${location.search}`;

  const { isFetching, data, error } = lostOpportunities;
  const maximumCtr = Math.max(100, ...data.map((row) => Number(row.ctr || 0) * 100));

  useEffect(() => {
    if (!currentShop) return;
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'lost_opportunities' }));
  }, [dispatch, currentShop, timeframe]);

  const handleTimeframeChange = (range) => {
    dispatch(analyticsActions.setTimeframe(range));
  };

  const headCells = [
    {
      id: 'query', numeric: false, label: localizeMessage('analytics.table.query'), sortKey: 'queryText',
    },
    {
      id: 'searches',
      numeric: true,
      label: localizeMessage('analytics.table.searches'),
      compact: true,
      width: 112,
    },
    {
      id: 'click',
      numeric: true,
      label: localizeMessage('analytics.table.click'),
      compact: true,
      width: 76,
    },
    {
      id: 'ctr',
      numeric: true,
      label: localizeMessage('analytics.table.ctr'),
      render: (value) => (
        <AnalyticsMetricBar
          value={value}
          maximum={maximumCtr}
          valueFormatter={(metric) => `${metric.toFixed(1)}%`}
          label={localizeMessage('analytics.table.ctr')}
          tone="lostOpportunities"
          compact
        />
      ),
      compact: true,
      width: 112,
    },
    detailEnabled && {
      id: 'edit',
      numeric: false,
      label: localizeMessage('analytics.table.settings'),
      visuallyHiddenLabel: true,
      action: true,
      sortable: false,
      width: 64,
    },
  ].filter(Boolean);

  const prepareRows = (rows) => rows.map((el, i) => ({
    ...el,
    queryText: el.query,
    detailTarget: detailEnabled
      ? buildQueryDetailTarget(currentShop, el.query, returnTo)
      : '',
    query: (
      <QueryDetailLink
        shopId={currentShop}
        query={el.query}
        enabled={detailEnabled}
        activeRuleCount={rulesForQuery(queryRuleIndex, el.query).active.length}
      />
    ),
    edit: (
      <QueryDetailEditAction
        shopId={currentShop}
        query={el.query}
        enabled={detailEnabled}
      />
    ),
    ctr: Number((el.ctr * 100).toFixed(1)),
    id: `${el.query}-${i}`,
  }));

  const handleRowActivate = (row) => {
    if (row.detailTarget) history.push(row.detailTarget);
  };

  return (
    <SearchFeaturePageShell
      pageId="analyticLostOpportunities"
      actions={(
        <AnalyticsReportActions
          exportEnabled={exportEnabled}
          shopId={currentShop}
          reportType="lost_opportunities"
          timeframe={timeframe}
          onSubmitRange={handleTimeframeChange}
          isFetching={isFetching}
          localizeMessage={localizeMessage}
        />
      )}
    >
      <Grid spacing={4} container>
        <Grid item xs={12}>
          <CardContainer
            headerId="search.analytics.lostOpportunities.header"
            subheaderId="search.analytics.lostOpportunities.subheader"
            styleName="noMargin"
            tableSurface
            isFetching={isFetching}
            error={error}
          >
            <AnalyticTable
              rows={prepareRows(data)}
              headCells={headCells}
              orderById="searches"
              fixedLayout
              interactiveRows={detailEnabled}
              onRowActivate={handleRowActivate}
              minWidth={660}
            />
          </CardContainer>
        </Grid>
      </Grid>
    </SearchFeaturePageShell>
  );
};

export default LostOpportunitiesBridge;
